import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { ImportarCategorias } from './ImportarCategorias'
import { PanelShell, PlnBarra, PlnBloco } from './PanelShell'
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiChevronRight, FiChevronDown,
  FiCornerDownRight, FiLoader, FiTag, FiUploadCloud, FiDownloadCloud,
} from 'react-icons/fi'

type Kind = 'receita' | 'despesa'
interface Category {
  id: number
  parent_id: number | null
  name: string
  kind: Kind
  code: string | null
  reduction_code: string | null
  is_system: 0 | 1
  auto_link_operation: string | null
  opens_split: 0 | 1
  dre_section: string | null
}

// Grupo DRE (migração 041): em que linha do DRE a categoria entra. A lista de
// seções vem do backend (/api/dre/secoes — fonte única), NULL herda da mãe.
interface DreSecaoOpt { key: string; label: string }

interface ModalState { mode: 'create' | 'edit'; kind: Kind; parentId: number | null; cat?: Category }

export function CategoriasFinanceiras() {
  const { authenticatedFetch } = useAuth()
  const [cats, setCats] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [modal, setModal] = useState<ModalState | null>(null)
  const [importing, setImporting] = useState(false)
  const [dreSecoes, setDreSecoes] = useState<DreSecaoOpt[]>([])

  useEffect(() => {
    authenticatedFetch('/api/dre/secoes').then(r => r.json())
      .then(d => {
        // 'nao_classificado' fica de fora do select: é o fallback de quem NÃO
        // escolheu, não uma escolha.
        if (d.success) setDreSecoes([...d.secoes, ...d.especiais.filter((e: DreSecaoOpt) => e.key === 'patrimonial')])
      })
      .catch(() => {})
  }, [authenticatedFetch])

  // Rótulo curto da seção (sem o prefixo "(−) "/"(+) ") pra tag e pro select.
  const dreShort = useCallback((k: string | null) => {
    if (!k) return ''
    const label = dreSecoes.find(s => s.key === k)?.label || k
    return label.replace(/^\([+−-]\)\s*/, '')
  }, [dreSecoes])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/categories')
      const d = await r.json()
      if (d.success) setCats(d.categories)
      else setError(d.error || 'Falha ao carregar')
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch])

  useEffect(() => { load() }, [load])

  // filtro por texto (mantém pais de filhos que casam)
  const filtered = useMemo(() => {
    if (!search.trim()) return cats
    const q = search.toLowerCase()
    const hit = new Set<number>()
    cats.forEach(c => {
      if (c.name.toLowerCase().includes(q) || (c.code || '').toLowerCase().includes(q)) {
        hit.add(c.id)
        if (c.parent_id) hit.add(c.parent_id)
      }
    })
    return cats.filter(c => hit.has(c.id))
  }, [cats, search])

  const roots = (kind: Kind) => filtered.filter(c => c.kind === kind && c.parent_id == null)
  const childrenOf = (id: number) => filtered.filter(c => c.parent_id === id)

  const toggle = (id: number) => setExpanded(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
  })

  // Export XLSX (2026-07-28). As 4 primeiras colunas usam de propósito os
  // rótulos que o auto-mapeamento de ImportarCategorias procura (Código /
  // Nome / Nível / Redz.), então o arquivo exportado volta pelo Importar sem
  // remapear nada à mão. As demais são informativas: o importador não lê tipo
  // (deduz do nome do sintético) nem "Abre rateio" — quem exporta, edita e
  // reimporta precisa remarcar o rateio na tela.
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const exportXLSX = useCallback(async () => {
    // Exporta o que está na tela: com busca ativa, sai o resultado filtrado.
    if (!filtered.length) return
    setExporting(true); setExportError(null)
    try {
      const XLSX = await import('xlsx')
      const kids = (id: number) => filtered.filter(c => c.parent_id === id)
      const linhas: Record<string, string>[] = []

      // Percorre em ordem de árvore (mãe seguida das filhas), igual à tela —
      // a ordem do /api/categories é por código e não mostra a hierarquia.
      const push = (c: Category, mae: Category | null) => {
        const filhas = kids(c.id)
        linhas.push({
          'Código': c.code || '',
          'Nome': c.name,
          'Nível': filhas.length > 0 ? 'Sintético' : 'Analítico',
          'Redz.': c.reduction_code || '',
          'Tipo': c.kind === 'receita' ? 'Receita' : 'Despesa',
          'Categoria mãe': mae?.name || '',
          'Abre rateio': c.opens_split === 1 ? 'Sim' : 'Não',
          'Padrão do sistema': c.is_system === 1 ? 'Sim' : 'Não',
        })
        filhas.forEach(f => push(f, c))
      }
      ;(['receita', 'despesa'] as Kind[]).forEach(k =>
        filtered.filter(c => c.kind === k && c.parent_id == null).forEach(c => push(c, null)))

      const ws = XLSX.utils.json_to_sheet(linhas)
      const cols = Object.keys(linhas[0])
      ws['!cols'] = cols.map(c => ({
        wch: Math.min(Math.max(c.length, ...linhas.map(l => String(l[c] ?? '').length)) + 2, 50),
      }))
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: cols.length - 1, r: linhas.length } }) }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Categorias')
      // Data local: toISOString() daria o dia errado antes das 21h no Brasil.
      const d = new Date()
      const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      XLSX.writeFile(wb, `categorias-financeiras_${stamp}.xlsx`)
    } catch (e) {
      // Pode ser o import lazy do xlsx bloqueado ou download sem permissão.
      console.error('[Categorias:export]', e)
      setExportError(e instanceof Error ? e.message : 'Falha ao exportar')
      window.setTimeout(() => setExportError(null), 5000)
    } finally {
      setExporting(false)
    }
  }, [filtered])

  const remove = async (cat: Category) => {
    if (!window.confirm(`Excluir a categoria "${cat.name}"?`)) return
    const r = await authenticatedFetch(`/api/categories/${cat.id}`, { method: 'DELETE' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Não foi possível excluir'); return }
    load()
  }

  const renderNode = (cat: Category, depth: number) => {
    const kids = childrenOf(cat.id)
    const isOpen = expanded.has(cat.id) || !!search.trim()
    return (
      <div key={cat.id}>
        <div
          className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-secondary/70 transition-colors"
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          {kids.length > 0 ? (
            <button onClick={() => toggle(cat.id)} className="text-muted-foreground hover:text-foreground shrink-0">
              {isOpen ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}
          {cat.code && <span className="text-xs font-mono text-muted-foreground shrink-0">{cat.code}</span>}
          {cat.reduction_code && (
            <span title="Código de redução (Redz.)" className="neu-convex text-[10px] font-mono text-muted-foreground/70 border border-border rounded px-1 shrink-0">
              red.{cat.reduction_code}
            </span>
          )}
          <span className="text-sm text-foreground truncate">{cat.name}</span>
          {cat.opens_split === 1 && (
            <span title="Abre o quadro de rateio automaticamente ao lançar uma despesa nesta categoria"
              className="neu-tag text-[10px] font-medium rounded-full px-1.5 py-0.5 leading-none shrink-0 bg-violet-500/10 text-violet-600 dark:text-violet-400">
              rateio
            </span>
          )}
          {cat.dre_section && (
            <span title={`Grupo DRE: ${dreShort(cat.dre_section)}${cat.parent_id ? ' (sobrescreve o grupo da categoria-mãe)' : ''}`}
              className={`neu-tag text-[10px] font-medium rounded-full px-1.5 py-0.5 leading-none shrink-0 truncate max-w-[150px] ${cat.dre_section === 'patrimonial' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-sky-500/10 text-sky-600 dark:text-sky-400'}`}>
              {dreShort(cat.dre_section)}
            </span>
          )}
          {cat.is_system === 1 && (
            <span className="neu-tag text-[10px] font-medium text-muted-foreground border border-border rounded-full px-1.5 py-0.5 leading-none shrink-0">
              Padrão
            </span>
          )}
          <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              title="Adicionar subcategoria"
              onClick={() => setModal({ mode: 'create', kind: cat.kind, parentId: cat.id })}
              className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-secondary"
            >
              <FiCornerDownRight className="w-3.5 h-3.5" />
            </button>
            {cat.is_system === 0 && (
              <>
                <button
                  title="Editar"
                  onClick={() => setModal({ mode: 'edit', kind: cat.kind, parentId: cat.parent_id, cat })}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
                >
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  title="Excluir"
                  onClick={() => remove(cat)}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
        {isOpen && kids.map(k => renderNode(k, depth + 1))}
      </div>
    )
  }

  const column = (kind: Kind, title: string) => {
    const list = roots(kind)
    const n = filtered.filter(c => c.kind === kind).length
    // PLANOS (21/10/2026): a coluna virou PlnBloco — título no cabeçalho do
    // bloco, contagem na nota e o "+ Nova" no `extra`.
    return (
      <PlnBloco
        titulo={title}
        nota={`${n} categoria(s)`}
        extra={
          <button
            onClick={() => setModal({ mode: 'create', kind, parentId: null })}
            className="neu-relief inline-flex items-center gap-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium px-2.5 py-1.5 hover:bg-primary/15 transition-colors"
          >
            <FiPlus className="w-3.5 h-3.5" /> Nova
          </button>
        }
        className="h-full min-h-0"
      >
        <div className="p-2 flex-1 min-h-[120px] overflow-y-auto scrollbar-thin">
          {list.length === 0
            ? <p className="text-sm text-muted-foreground text-center py-8">Nenhuma categoria{search ? ' encontrada' : ''}.</p>
            : list.map(c => renderNode(c, 0))}
        </div>
      </PlnBloco>
    )
  }

  return (
    <PanelShell
      titulo="Categorias financeiras"
      sub="O plano de contas: categorias de receita e despesa e o grupo de cada uma na DRE"
      icone={<FiTag />}
      acoes={<>
        <button
          onClick={exportXLSX}
          disabled={exporting || loading || filtered.length === 0}
          title={search.trim()
            ? `Exportar as ${filtered.length} categorias do filtro para Excel`
            : `Exportar as ${filtered.length} categorias para Excel`}
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {exporting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownloadCloud className="w-4 h-4" />}
          Exportar
        </button>
        <button
          onClick={() => setImporting(true)}
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary"
        >
          <FiUploadCloud className="w-4 h-4" /> Importar
        </button>
      </>}>
      {/* Busca — PLANOS (21/10/2026): numa PlnBarra; Exportar/Importar subiram
          para as ações do cabeçalho. */}
      <PlnBarra>
        <div className="relative max-w-sm flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar categoria…"
            className="neu-convex w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
      </PlnBarra>

      {exportError && <div className="shrink-0 text-sm text-red-500">Exportar: {exportError}</div>}
      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <FiLoader className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        /* Colunas de altura cheia — cada árvore rola por dentro */
        <div className="flex-1 min-h-0 grid gap-4 md:grid-cols-2 auto-rows-fr">
          {column('receita', 'Categorias de receita')}
          {column('despesa', 'Categorias de despesa')}
        </div>
      )}

      {modal && (
        <CategoryModal
          state={modal}
          cats={cats}
          dreSecoes={dreSecoes}
          dreShort={dreShort}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
        />
      )}

      {importing && (
        <ImportarCategorias onClose={() => setImporting(false)} onImported={() => { setImporting(false); load() }} />
      )}
    </PanelShell>
  )
}

function CategoryModal({ state, cats, dreSecoes, dreShort, onClose, onSaved }: {
  state: ModalState; cats: Category[]; dreSecoes: DreSecaoOpt[]; dreShort: (k: string | null) => string
  onClose: () => void; onSaved: () => void
}) {
  const { authenticatedFetch } = useAuth()
  const [name, setName] = useState(state.cat?.name || '')
  const [code, setCode] = useState(state.cat?.code || '')
  const [reductionCode, setReductionCode] = useState(state.cat?.reduction_code || '')
  const [opensSplit, setOpensSplit] = useState(!!state.cat?.opens_split)
  const [dreSection, setDreSection] = useState(state.cat?.dre_section || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const parent = state.parentId ? cats.find(c => c.id === state.parentId) : null
  const kindLabel = state.kind === 'receita' ? 'receita' : 'despesa'
  const title = state.mode === 'edit' ? 'Editar categoria' : parent ? 'Nova subcategoria' : `Nova categoria de ${kindLabel}`

  const save = async () => {
    if (!name.trim()) { setErr('Informe o nome.'); return }
    setSaving(true); setErr(null)
    try {
      const r = state.mode === 'edit'
        ? await authenticatedFetch(`/api/categories/${state.cat!.id}`, { method: 'PUT', body: JSON.stringify({ name, code, reduction_code: reductionCode, opens_split: opensSplit, dre_section: dreSection || null }) })
        : await authenticatedFetch('/api/categories', { method: 'POST', body: JSON.stringify({ name, code, reduction_code: reductionCode, opens_split: opensSplit, dre_section: dreSection || null, kind: state.kind, parent_id: state.parentId }) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao salvar'); return }
      onSaved()
    } catch {
      setErr('Erro de conexão')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SideModal
      title={title}
      subtitle={parent ? `Dentro de: ${parent.name}` : 'Categoria financeira'}
      icon={FiTag}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button
            onClick={save} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
          >
            {saving && <FiLoader className="w-4 h-4 animate-spin" />}
            Salvar
          </button>
        </>
      }
    >
      {err && <div className="text-sm text-red-500">{err}</div>}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Nome*</label>
        <input
          autoFocus value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && save()}
          className="neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          placeholder="Ex.: Consultoria financeira"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Código (opcional)</label>
          <input
            value={code} onChange={e => setCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && save()}
            className="neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            placeholder="Ex.: 3.01.01"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Cód. de redução (opcional)</label>
          <input
            value={reductionCode} onChange={e => setReductionCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && save()}
            className="neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            placeholder="Ex.: 95"
          />
        </div>

        {/* Grupo DRE (2026-07-31, pedido da diretoria): em que linha do DRE os
            lançamentos desta categoria entram. Vazio = herda da categoria-mãe;
            "Movimentação patrimonial" tira do resultado (empréstimo, aplicação,
            imobilizado, distribuição de lucro). É aqui que a diretoria ajusta a
            classificação sem mexer em código. */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1">Grupo DRE</label>
          <select
            value={dreSection} onChange={e => setDreSection(e.target.value)}
            className="neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <option value="">
              {parent
                ? `(herdar do grupo: ${dreShort(parent.dre_section) || 'não classificado'})`
                : '(não classificado)'}
            </option>
            {dreSecoes.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Define a linha do DRE comparativo (Relatórios). "Movimentação patrimonial"
            fica fora do resultado — use para empréstimo, aplicação/resgate, imobilizado
            e distribuição de lucro.
          </p>
        </div>

        {/* Rateio por categoria (2026-07-28). Antes quem decidia se o quadro de
            rateio abria era uma regex no NOME (/cart[ãa]o/) — servia pra fatura
            de cartão e mais nada. JVN SERVIÇOS é o mesmo caso (um pagamento que
            é a soma de vários serviços) e nunca casaria com "cartão". Agora é
            escolha de quem cadastra, e vale pra qualquer categoria futura. */}
        {state.kind === 'despesa' && (
          <div className="neu-convex rounded-lg border border-border p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={opensSplit}
                onChange={e => setOpensSplit(e.target.checked)}
                className="neu-check mt-0.5 w-4 h-4 accent-primary cursor-pointer shrink-0"
              />
              <span>
                <span className="block text-sm font-medium text-foreground">Abrir rateio ao lançar</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">
                  Para pagamento único que é a soma de vários itens — fatura de cartão,
                  nota de serviços. Ao escolher esta categoria em Nova despesa, o quadro
                  de rateio já vem aberto para dizer do que o valor é composto.
                  O rateio continua opcional e disponível em qualquer despesa.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>
    </SideModal>
  )
}
